'use client';

import { useCallback, useState } from 'react';

const STORAGE_KEY = 'safha:recent-searches';
const MAX_ENTRIES = 6;

function readStored(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((t): t is string => typeof t === 'string')
      : [];
  } catch {
    // Corrupt or unavailable storage — start empty.
    return [];
  }
}

/**
 * Recent search terms, persisted client-side in localStorage. The backend
 * doesn't store per-user search history, so this lives entirely on the client
 * — most-recent-first, de-duplicated, capped at MAX_ENTRIES. The lazy
 * initializer (SSR-guarded) mirrors `useDataView` and avoids setting state in
 * a mount effect.
 */
export function useRecentSearches() {
  const [recent, setRecent] = useState<string[]>(readStored);

  const add = useCallback((term: string) => {
    const trimmed = term.trim();
    if (!trimmed) return;
    setRecent((prev) => {
      const next = [trimmed, ...prev.filter((t) => t !== trimmed)].slice(
        0,
        MAX_ENTRIES
      );
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        // Ignore quota / unavailable storage.
      }
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    setRecent([]);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Ignore.
    }
  }, []);

  return { recent, add, clear };
}
